import React from "react";
import { useIntl } from "react-intl";

import { ColumnProps } from "src/common/components/DataTable";
import { MobileColumnProps } from "src/common/components/MobileTable";
import { SocialSecurityNumber } from "src/common/components/SocialSecurityNumber";
import { StatusBadge } from "src/common/components/StatusBadge";
import { messages } from "src/modules/kd/patient-list/messages";
import {
	PatientStatus,
	WaitingListTime
} from "src/modules/kd/patient-list/types";
import {
	compareNumberEvents,
	mapNumberEventToString,
	renderNumberEvent
} from "src/modules/shared/patient-list";

export function useDesktopColumnProps(): ColumnProps[] {
	const modifTime = useModifTime();
	const name = useNameColumn();
	const status = useStatusColumnDesktop();
	const socialSecurityNumber = useSocialSecurityNumberColumn();
	const weight = useWeightColumn();
	const bloodGroup = useBloodGroupColumn();

	const age = useAgeColumn();
	const sex = useSexColumn();
	const city = useCityColumn();

	const waitingList = useWaitingListColumnDesktop();
	const statusByNephrologist = useStatusByNephrologist();

	return React.useMemo(
		() => [
			modifTime,
			name,
			status,
			socialSecurityNumber,
			age,
			sex,
			city,
			weight,
			bloodGroup,
			waitingList,
			statusByNephrologist
		],
		[
			modifTime,
			name,
			status,
			socialSecurityNumber,
			age,
			sex,
			city,
			weight,
			bloodGroup,
			waitingList,
			statusByNephrologist
		]
	);
}

export function useMobileColumnProps(): MobileColumnProps {
	const modifTime = useModifTime();
	const status = useStatusColumnMobile();
	const name = useNameColumn();
	const socialSecurityNumber = useSocialSecurityNumberColumn();

	const bloodGroup = useBloodGroupColumn();
	const age = useAgeColumn();
	const waitingList = useWaitingListColumnMobile();
	const weight = useWeightColumn();
	const statusByNephrologist = useStatusByNephrologist();

	return React.useMemo(
		() => ({
			icon: status,
			title: name,
			subtitle: socialSecurityNumber,
			fix: [
				modifTime,
				bloodGroup,
				age,
				waitingList,
				weight,
				statusByNephrologist
			],
			optional: []
		}),
		[
			modifTime,
			name,
			status,
			socialSecurityNumber,
			bloodGroup,
			age,
			waitingList,
			weight,
			statusByNephrologist
		]
	);
}

function useSexColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({ key: "sex", label: formatMessage(messages.sex) }),
		[formatMessage]
	);
}

function useCityColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({ key: "city", label: formatMessage(messages.city) }),
		[formatMessage]
	);
}

function useAgeColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "age",
			label: formatMessage(messages.ageLabel),
			render(value: number): React.ReactNode {
				return (
					<>
						<b>{value}</b> <i>{formatMessage(messages.ageUnit)}</i>
					</>
				);
			},
			mapToString(value: number): string {
				return `${value} ${formatMessage(messages.ageUnit)}`;
			},
			compare(a: number, b: number): number {
				if (a < b) {
					return -1;
				}
				if (a > b) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}

function useStatusColumnDesktop(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "status",
			label: formatMessage(messages.status),
			render({ value, longLabel }: PatientStatus): React.ReactNode {
				return <StatusBadge value={value} label={longLabel} />;
			},
			mapToString({ longLabel }: PatientStatus): string {
				return longLabel;
			}
		}),
		[formatMessage]
	);
}

function useStatusColumnMobile(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "status",
			label: formatMessage(messages.status),
			render({ value, shortLabel }: PatientStatus): React.ReactNode {
				return <StatusBadge value={value} label={shortLabel} />;
			},
			mapToString({ shortLabel }: PatientStatus): string {
				return shortLabel;
			}
		}),
		[formatMessage]
	);
}

function useStatusByNephrologist(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "statusByNephrologist",
			label: formatMessage(messages.statusByNephrologist)
		}),
		[formatMessage]
	);
}

function useNameColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "name",
			label: formatMessage(messages.name)
		}),
		[formatMessage]
	);
}

function useSocialSecurityNumberColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "socialSecurityNumber",
			label: formatMessage(messages.socialSecurityNumber),
			render(value: string): React.ReactNode {
				return <SocialSecurityNumber>{value}</SocialSecurityNumber>;
			}
		}),
		[formatMessage]
	);
}

function useModifTime(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "modifTime",
			label: formatMessage(messages.modifTime),
			compare(a: string, b: string): number {
				const bDate = new Date(b).getTime();
				const aDate = new Date(a).getTime();

				if (aDate < bDate) {
					return -1;
				}

				if (aDate > bDate) {
					return 1;
				}

				return 0;
			}
		}),
		[formatMessage]
	);
}

function useWeightColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "weight",
			label: formatMessage(messages.weight),
			render: renderNumberEvent,
			mapToString: mapNumberEventToString,
			compare: compareNumberEvents
		}),
		[formatMessage]
	);
}

function useBloodGroupColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "bloodGroup",
			label: formatMessage(messages.bloodGroup)
		}),
		[formatMessage]
	);
}

function useWaitingListColumnDesktop(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "waitingListTime",
			label: formatMessage(messages.waitingListLabel),
			render(value: WaitingListTime | undefined): React.ReactNode {
				if (value === undefined) {
					return null;
				}
				if (value.months > 1) {
					return (
						<div>
							<b>{Math.round(value.months)}</b>
							{". "}
							<i>
								{formatMessage(
									messages.waitingListMonthsUnitDesktop
								)}
							</i>
						</div>
					);
				} else {
					return (
						<div>
							<b>{value.days}</b>{" "}
							<i>
								{formatMessage(
									messages.waitingListDaysUnitDesktop
								)}
							</i>
						</div>
					);
				}
			},
			mapToString(value: WaitingListTime | undefined): string {
				if (value && value.months > 1) {
					return `${value.months} ${formatMessage(
						messages.waitingListMonthsUnitDesktop
					)}`;
				} else {
					return `${value ? value.days : "-"} ${formatMessage(
						messages.waitingListDaysUnitDesktop
					)}`;
				}
			},
			compare(
				a: WaitingListTime | undefined,
				b: WaitingListTime | undefined
			): number {
				if (a === undefined && b === undefined) {
					return 0;
				}
				if (a === undefined) {
					return 1;
				}
				if (b === undefined) {
					return -1;
				}
				if (a.days < b.days) {
					return -1;
				}
				if (a.days > b.days) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}

function useWaitingListColumnMobile(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "waitingListTime",
			label: formatMessage(messages.waitingListLabel),
			render(value: WaitingListTime | undefined): React.ReactNode {
				if (value === undefined) {
					return "-";
				}

				if (value.months > 1) {
					return (
						<div>
							<b>{Math.round(value.months)}</b>
							{". "}
							<i>
								{formatMessage(
									messages.waitingListMonthsUnitMobile
								)}
							</i>
						</div>
					);
				} else {
					return (
						<div>
							<b>{value.days}</b>{" "}
							<i>
								{formatMessage(
									messages.waitingListDaysUnitMobile
								)}
							</i>
						</div>
					);
				}
			},
			mapToString(value: WaitingListTime | undefined): string {
				if (value && value.months > 1) {
					return `${value.months} ${formatMessage(
						messages.waitingListMonthsUnitDesktop
					)}`;
				} else {
					return `${value ? value.days : "-"} ${formatMessage(
						messages.waitingListDaysUnitDesktop
					)}`;
				}
			},
			compare(
				a: WaitingListTime | undefined,
				b: WaitingListTime | undefined
			): number {
				if (a === undefined && b === undefined) {
					return 0;
				}
				if (a === undefined) {
					return 1;
				}
				if (b === undefined) {
					return -1;
				}
				if (a.days < b.days) {
					return -1;
				}
				if (a.days > b.days) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}
